/**
 * Hook del listado de Clientes con paginación server-side (US-2.3).
 *
 * Espejo de `useAllies`: filtros + `offset`/`limit` + `meta` del backend, con
 * loading/error/refresh. La red vive en `customerApi`; aquí solo el estado de la
 * vista (separable de la presentación).
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  Customer,
  CustomerStatus,
  CustomerType,
  PageMeta,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { customerApi, type CustomerListParams } from '../api/customerApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_CUSTOMERS_LIMIT = 20;

/** Filtros del listado (los del contrato, sin paginación). */
export interface CustomerFilters {
  q?: string;
  status?: CustomerStatus;
  /** `CONDUCTOR` (cliente individual) o `FLOTA` (Cuenta Maestra corporativa). */
  tipoCliente?: CustomerType;
  /** Filtra por aliado; el contrato lo hace efectivo solo para `SUPER_ADMIN`. */
  aliadoId?: string;
}

export interface UseCustomersOptions {
  /** Elementos por página (default `DEFAULT_CUSTOMERS_LIMIT`). */
  limit?: number;
  /** Filtros iniciales. */
  initialFilters?: CustomerFilters;
}

export interface UseCustomersResult {
  /** Items del listado con su estado de cuenta. */
  data: Customer[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  /** Filtros activos. */
  filters: CustomerFilters;
  /** Desplazamiento actual (paginación server-side). */
  offset: number;
  limit: number;
  /** Aplica filtros nuevos y reinicia la paginación a la primera página. */
  setFilters: (filters: CustomerFilters) => void;
  /** Mueve la página (offset) actual. */
  setOffset: (offset: number) => void;
  /** Recarga la página actual (p. ej. tras crear un cliente o ajustar saldo). */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar los clientes.';
}

export function useCustomers(options: UseCustomersOptions = {}): UseCustomersResult {
  const limit = options.limit ?? DEFAULT_CUSTOMERS_LIMIT;
  const [filters, setFiltersState] = useState<CustomerFilters>(() => options.initialFilters ?? {});
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<Customer[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: CustomerListParams = { ...filters, offset, limit };
    customerApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [filters, offset, limit, reloadKey]);

  const setFilters = useCallback((next: CustomerFilters) => {
    setOffsetState(0);
    setFiltersState(next);
  }, []);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, filters, offset, limit, setFilters, setOffset, refresh };
}
